import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { adminApi } from '../services/api';

interface AdminReview {
  review_id: string;
  user_info: {
    nickname: string;
    email: string;
    is_active: boolean;
  };
  address: string;
  ratings: {
    childcare_education: number;
    medical_welfare: number;
    transportation: number;
    shopping_commercial: number;
    living_environment: number;
    government_services: number;
  };
  comments: {
    childcare_education: string;
    medical_welfare: string;
    transportation: string;
    shopping_commercial: string;
    living_environment: string;
    government_services: string;
  };
  created_at: string;
  is_deleted?: boolean;
}

interface ReviewsResponse {
  reviews: AdminReview[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

const AdminReviews: React.FC = () => {
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userIdFilter, setUserIdFilter] = useState('');
  
  // 削除ダイアログ状態
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    review: AdminReview | null;
  }>({ open: false, review: null });
  
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [page, userIdFilter]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 20 };
      if (userIdFilter) {
        params.user_id = userIdFilter;
      }
      
      const response: ReviewsResponse = await adminApi.getReviews(params);
      setReviews(response.reviews);
      setTotalPages(Math.ceil(response.total / 20));
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || '口コミ一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!deleteDialog.review || !deleteReason) return;

    try {
      setDeleteLoading(true);
      await adminApi.deleteReview(deleteDialog.review.review_id, deleteReason);
      
      setDeleteDialog({ open: false, review: null });
      setDeleteReason('');
      await fetchReviews();
    } catch (err: any) {
      setError(err.response?.data?.error || '口コミの削除に失敗しました');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getAverageRating = (ratings: AdminReview['ratings']) => {
    const values = Object.values(ratings);
    const average = values.reduce((sum, rating) => sum + rating, 0) / values.length;
    return average.toFixed(1);
  };

  const getStatusChip = (review: AdminReview) => {
    if (review.is_deleted) {
      return <Chip label="削除済み" color="error" size="small" />;
    }
    return <Chip label="公開中" color="success" size="small" />;
  };

  const getUserStatusChip = (userInfo: AdminReview['user_info']) => {
    if (!userInfo.is_active) {
      return <Chip label="停止中ユーザー" color="warning" size="small" />;
    }
    return <Chip label="アクティブユーザー" color="default" size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        口コミ管理
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box mb={2}>
        <TextField
          label="ユーザーIDでフィルター"
          value={userIdFilter}
          onChange={(e) => setUserIdFilter(e.target.value)}
          size="small"
          sx={{ mr: 2 }}
        />
        <Button variant="outlined" onClick={() => setUserIdFilter('')}>
          フィルタークリア
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>投稿者</TableCell>
              <TableCell>ユーザーステータス</TableCell>
              <TableCell>住所</TableCell>
              <TableCell>平均評価</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>投稿日</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((review) => (
              <TableRow key={review.review_id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {review.user_info.nickname}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {review.user_info.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{getUserStatusChip(review.user_info)}</TableCell>
                <TableCell>{review.address}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" fontWeight="bold">
                      ★{getAverageRating(review.ratings)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{getStatusChip(review)}</TableCell>
                <TableCell>
                  {new Date(review.created_at).toLocaleDateString('ja-JP')}
                </TableCell>
                <TableCell>
                  {!review.is_deleted && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, review })}
                    >
                      削除
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
        />
      </Box>

      {/* 削除ダイアログ */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, review: null })}>
        <DialogTitle>口コミ削除</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            この口コミを削除しますか？
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            投稿者: {deleteDialog.review?.user_info.nickname}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            住所: {deleteDialog.review?.address}
          </Typography>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>削除理由</InputLabel>
            <Select
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              required
            >
              <MenuItem value="利用規約違反">利用規約違反</MenuItem>
              <MenuItem value="不適切な内容">不適切な内容</MenuItem>
              <MenuItem value="虚偽の情報">虚偽の情報</MenuItem>
              <MenuItem value="スパム">スパム</MenuItem>
              <MenuItem value="その他">その他</MenuItem>
            </Select>
          </FormControl>
          
          {deleteReason === 'その他' && (
            <TextField
              fullWidth
              label="詳細理由"
              multiline
              rows={3}
              margin="normal"
              placeholder="詳細な削除理由を入力してください"
            />
          )}
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            削除されたユーザーにはメール通知が送信されます。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, review: null })}>
            キャンセル
          </Button>
          <Button 
            onClick={handleDeleteReview} 
            disabled={deleteLoading || !deleteReason}
            color="error"
          >
            {deleteLoading ? <CircularProgress size={20} /> : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminReviews;
